import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import BenchmarkChip from "../components/BenchmarkChip";
import {
  hasGrounding,
  useCitationsMap,
  GroundedText,
  ConfidenceSummaryCard,
  RecommendationCards,
  RegulatoryObligationsPanel,
  SourcesSection,
} from "../components/GroundingWidgets";
import {
  Download, MessageCircle, Calendar, Mail, Clock, ShieldCheck, Sparkles,
  Loader2, RefreshCw, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Pack 3 · Deliverable 2 — Executive Growth Report™.
 *
 * Structure (as approved in the MLP Blueprint):
 *   1. Executive Summary
 *   2. Business Growth Index™
 *   3. Key Business Opportunities
 *   4. Opportunity Analysis
 *   5. Benchmark Insights
 *   6. Priority Action Roadmap (30 / 90 / 180 days)
 *   7. Consultant Perspective™
 *   8. Recommended Next Step
 *
 * Business leaders (unauthenticated) only see the full report once the
 * consultant has approved it. Until then, they see an "Awaiting review"
 * state that mirrors the Executive Snapshot they already saw on Results.
 *
 * Pack 5 · Report generation is now async — this page polls GET /leads/{id}
 * every 3s while `report_status === "generating"` and surfaces a
 * "regenerate" affordance to the owning consultant if the AI job ends in
 * `report_status === "failed"`.
 */
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 180000; // 3 minutes — well beyond the 3-attempt AI budget

export default function Report({ sampleData = null }) {
  const { id } = useParams();
  const [data, setData] = useState(sampleData);
  const [status, setStatus] = useState("approved");
  const [isOwner, setIsOwner] = useState(Boolean(sampleData));
  const [regenerating, setRegenerating] = useState(false);
  const pollStartRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await api.get(`/leads/${id}`);
      const lead = r.data.lead;
      const rs = r.data.report_status || (lead?.report_status ?? "approved");
      setStatus(rs);
      setIsOwner(Boolean(r.data.is_owner));
      setData({
        lead_id: id,
        report: lead?.report,
        results: lead?.results,
        assessment: { country: lead?.country, industry: lead?.industry, function: lead?.function },
        consultant: r.data.consultant,
        firmographics: r.data.firmographics || {},
        consultant_perspective: lead?.consultant_perspective || "",
        // Pack 4 · use backend-authored message templates so the Discovery
        // Meeting request text is identical to what the business leader saw
        // at capture time. Never rebuild a bare wa.me/<digits> here — that
        // was the source of item #9 (WhatsApp button with no message).
        whatsapp_link: r.data.whatsapp_link || "",
        mailto_link: r.data.mailto_link || "",
        booking_link: r.data.consultant?.booking_link,
        // Pack 13.10 · read-side grounding envelope (null for legacy leads).
        grounding: r.data._grounding || null,
      });
      return rs;
    } catch {
      setData({ error: true });
      return "error";
    }
  }, [id]);

  useEffect(() => {
    if (sampleData) return;
    fetchOnce();
  }, [fetchOnce, sampleData]);

  // Pack 5 · poll while the AI job is running. Stops on any terminal state
  // (pending_review, approved, failed) or if we exceed POLL_MAX_MS.
  useEffect(() => {
    if (sampleData) return;
    if (status !== "generating") return;
    if (pollStartRef.current === null) pollStartRef.current = Date.now();
    const timer = setInterval(async () => {
      if (Date.now() - pollStartRef.current > POLL_MAX_MS) {
        clearInterval(timer);
        return;
      }
      await fetchOnce();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [status, fetchOnce, sampleData]);

  const regenerate = useCallback(async () => {
    setRegenerating(true);
    try {
      await api.post(`/leads/${id}/regenerate-report`);
      toast.success("Report generation restarted");
      setStatus("generating");
      pollStartRef.current = Date.now();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRegenerating(false);
    }
  }, [id]);

  // Pack 13.10 · citation lookup map. Hook must be called before any
  // conditional returns to satisfy rules-of-hooks.
  const grounding = data?.report ? (data.grounding || null) : (data?.grounding || null);
  const citationsMap = useCitationsMap(grounding);

  if (!data) return <div className="p-10 text-muted-foreground">Loading report…</div>;
  if (data.error) return <div className="p-10 text-muted-foreground">Report not found.</div>;

  const { results, assessment, consultant, firmographics = {} } = data;
  const report = data.report;
  const groundingActive = hasGrounding(grounding);

  // Pack 5 states — surfaced BEFORE the pending-review gate:
  //   generating → both leader + consultant see a "preparing" state (polls)
  //   failed     → consultant sees a retry CTA; leader sees a friendly note
  if (status === "generating") {
    return <GeneratingState assessment={assessment} data={data} />;
  }
  if (status === "failed") {
    return (
      <FailedState
        assessment={assessment}
        data={data}
        isOwner={isOwner}
        regenerating={regenerating}
        onRetry={regenerate}
      />
    );
  }

  // Pending review — show gate (business leader only). The owning consultant
  // always sees the full report so they can review it.
  const gated = status !== "approved" && !isOwner;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="executive-growth-report">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline"><span className="accent-line" /> Executive Growth Report™</div>
          <h1 className="font-display text-4xl mt-3">Your Executive Growth Report™</h1>
          <p className="text-muted-foreground mt-2">{assessment.country} · {assessment.industry} · {assessment.function}</p>
        </div>
        {!gated && !sampleData && (
          <a href={`${API}/leads/${id}/pdf`} target="_blank" rel="noreferrer">
            <Button data-testid="report-download-pdf" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-6">
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </a>
        )}
      </div>

      {gated ? (
        <PendingReviewState consultant={consultant} results={results} data={data} />
      ) : (
        <>
          {/* Pack 13.10 · Confidence chip (grounded reports only) */}
          {groundingActive && (
            <div className="mt-8" data-testid="report-section-confidence">
              <ConfidenceSummaryCard summary={grounding.confidence_summary} />
            </div>
          )}

          {/* 1. Executive Summary */}
          <Section title="1. Executive Summary" testId="report-section-exec">
            <p className="text-base leading-relaxed">
              {groundingActive
                ? <GroundedText text={report?.executive_summary} citationsMap={citationsMap} />
                : report?.executive_summary}
            </p>
          </Section>

          {/* 2. Business Growth Index™ */}
          <Section title="2. Business Growth Index™" testId="report-section-index">
            <div className="grid md:grid-cols-3 gap-5">
              <Card className="card-elevated p-6">
                <div className="overline">Growth Index</div>
                <div className="font-display text-6xl text-accent mt-2">{results.overall_score}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">
                  {results.risk_level} performance
                </div>
              </Card>
              <Card className="card-elevated p-6 md:col-span-2">
                <div className="overline">Category scores</div>
                <div className="mt-3 space-y-3">
                  {Object.entries(results.category_scores).map(([k, v]) => (
                    <div key={k}>
                      <div className="flex items-center justify-between text-sm">
                        <span>{k}</span>
                        <span className="text-muted-foreground">{v}/100</span>
                      </div>
                      <div className="h-1.5 bg-secondary mt-2 rounded-full overflow-hidden">
                        <div className="h-full bg-accent" style={{ width: `${v}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </Section>

          {/* 3. Key Business Opportunities */}
          <Section title="3. Key Business Opportunities" testId="report-section-opportunities">
            <div className="grid md:grid-cols-3 gap-4">
              {(results.top_gaps || []).map((g, i) => (
                <Card key={g.area} className="card-elevated p-5">
                  <div className="text-xs uppercase tracking-widest text-accent">Opportunity #{i + 1}</div>
                  <div className="font-sans-ui font-semibold mt-2">{g.area}</div>
                  <div className="text-sm text-muted-foreground mt-2">Current score: {g.score}/100</div>
                </Card>
              ))}
            </div>
          </Section>

          {/* 4. Opportunity Analysis */}
          <Section title="4. Opportunity Analysis" testId="report-section-analysis">
            <div className="grid md:grid-cols-2 gap-4">
              {report?.gap_analysis?.map((g, i) => (
                <Card key={`${g.area}-${i}`} className="card-elevated p-5">
                  <div className="overline">{g.area}</div>
                  <div className="mt-2 font-sans-ui font-semibold">
                    {groundingActive
                      ? <GroundedText text={g.finding} citationsMap={citationsMap} />
                      : g.finding}
                  </div>
                  <div className="text-sm text-muted-foreground mt-2">Impact: {g.impact}</div>
                </Card>
              ))}
            </div>
            {report?.key_risks?.length ? (
              <div className="mt-6">
                <div className="overline">Key risks</div>
                <ul className="mt-3 space-y-2">
                  {report.key_risks.map((r, i) => (
                    <li key={`risk-${i}`} className="flex items-start gap-3">
                      <span className="text-accent mt-1">▍</span>
                      <span>
                        {groundingActive
                          ? <GroundedText text={r} citationsMap={citationsMap} />
                          : r}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Section>

          {/* Pack 13.10 · Grounded Recommendation Cards + Regulatory Obligations */}
          {groundingActive && (grounding.recommendation_cards || []).length > 0 && (
            <Section title="Grounded Recommendations" testId="report-section-recommendations">
              <RecommendationCards cards={grounding.recommendation_cards} />
            </Section>
          )}
          {groundingActive && (grounding.regulatory_obligations || []).length > 0 && (
            <Section title="Regulatory Obligations" testId="report-section-regulatory">
              <RegulatoryObligationsPanel obligations={grounding.regulatory_obligations} />
            </Section>
          )}

          {/* 5. Benchmark Insights */}
          <Section title="5. Benchmark Insights" testId="report-section-benchmark">
            <Card className="card-elevated p-6">
              <div className="overline">How you compare against your peers</div>
              <div className="mt-4">
                <BenchmarkChip
                  country={assessment.country}
                  industry={assessment.industry}
                  fn={assessment.function}
                  sizeBand={firmographics.prospect_company_size_band}
                  score={results.overall_score}
                />
              </div>
              <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                Benchmarks are anonymised averages across BizPulse AI diagnostics in the same {assessment.industry} segment in {assessment.country}. Use the gap to prioritise the roadmap below.
              </p>
            </Card>
          </Section>

          {/* 6. Priority Action Roadmap */}
          <Section title="6. Priority Action Roadmap" testId="report-section-roadmap">
            <PriorityRoadmap report={report} />
          </Section>

          {/* 7. Consultant Perspective™ */}
          <Section title="7. Consultant Perspective™" testId="report-section-perspective">
            <Card className="card-elevated p-6">
              {data.consultant_perspective?.trim() ? (
                <p className="text-base leading-relaxed whitespace-pre-line">{data.consultant_perspective}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Your consultant has approved this report without adding a written perspective. They will share their view in your discovery meeting.
                </p>
              )}
              <div className="mt-5 flex items-center gap-3 pt-4 border-t border-border/60">
                {consultant?.profile_photo ? (
                  <img src={consultant.profile_photo} alt={consultant.name} className="w-10 h-10 rounded-full object-cover border border-border" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent font-semibold">{consultant?.name?.[0]}</div>
                )}
                <div>
                  <div className="font-sans-ui font-semibold text-sm">{consultant?.name}</div>
                  <div className="text-xs text-muted-foreground">{consultant?.company}</div>
                </div>
              </div>
            </Card>
          </Section>

          {/* 8. Recommended Next Step */}
          <Section title="8. Recommended Next Step" testId="report-section-next-step">
            <Card className="card-elevated p-7 flex flex-col md:flex-row gap-6 items-start">
              {consultant?.profile_photo ? (
                <img src={consultant.profile_photo} alt={consultant.name} className="w-24 h-24 rounded-full object-cover border border-border" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-accent/20 flex items-center justify-center text-accent text-2xl font-semibold">{consultant?.name?.[0]}</div>
              )}
              <div className="flex-1">
                <div className="font-sans-ui font-semibold text-lg">{consultant?.name}</div>
                <div className="text-muted-foreground text-sm">{consultant?.company}</div>
                {consultant?.biography && <p className="text-sm mt-3 leading-relaxed">{consultant.biography}</p>}
                <div className="flex flex-wrap gap-3 mt-5">
                  {data.whatsapp_link && (
                    <a href={data.whatsapp_link} target="_blank" rel="noreferrer">
                      <Button data-testid="report-whatsapp" className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
                        <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                      </Button>
                    </a>
                  )}
                  {data.booking_link && (
                    <a href={data.booking_link} target="_blank" rel="noreferrer">
                      <Button data-testid="report-booking" variant="outline" className="rounded-full">
                        <Calendar className="w-4 h-4 mr-2" /> Book a call
                      </Button>
                    </a>
                  )}
                  {(data.mailto_link || consultant?.email) && (
                    <a href={data.mailto_link || `mailto:${consultant.email}`}>
                      <Button variant="outline" data-testid="report-email" className="rounded-full">
                        <Mail className="w-4 h-4 mr-2" /> Email
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            </Card>
          </Section>

          {/* Pack 13.10 · Sources & Evidence appendix (grounded reports only) */}
          {groundingActive && (
            <Section title="9. Sources & Evidence" testId="report-section-sources">
              <SourcesSection
                citations={grounding.citations}
                pipelineVersion={grounding.pipeline_version}
                promptFingerprint={grounding.prompt_fingerprint}
              />
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <div className="mt-10" data-testid={testId}>
      <div className="overline"><span className="accent-line" /> {title}</div>
      <h2 className="font-display text-3xl mt-3 mb-5">{title}</h2>
      {children}
    </div>
  );
}

/**
 * Priority Action Roadmap — 30 / 90 / 180 day columns.
 * Prefers the AI-produced `report.roadmap`; falls back to bucketing
 * `priority_improvements` by P0/P1/P2 so legacy reports still render.
 */
function PriorityRoadmap({ report }) {
  const roadmap = useMemo(() => {
    const rm = report?.roadmap || {};
    const has = ["next_30_days", "next_90_days", "next_180_days"].some((k) => (rm[k] || []).length);
    if (has) return rm;
    // Legacy fallback: bucket priority_improvements by P0/P1/P2
    const p = report?.priority_improvements || [];
    return {
      next_30_days: p.filter((x) => x.priority === "P0").map((x) => ({ title: x.title, detail: x.rationale })),
      next_90_days: p.filter((x) => x.priority === "P1").map((x) => ({ title: x.title, detail: x.rationale })),
      next_180_days: p.filter((x) => x.priority === "P2").map((x) => ({ title: x.title, detail: x.rationale })),
    };
  }, [report]);

  const columns = [
    { key: "next_30_days",  label: "Next 30 days",  icon: Clock,       tone: "text-red-300",     ring: "border-red-500/30 bg-red-500/5" },
    { key: "next_90_days",  label: "Next 90 days",  icon: ShieldCheck, tone: "text-amber-300",   ring: "border-amber-500/30 bg-amber-500/5" },
    { key: "next_180_days", label: "Next 180 days", icon: Sparkles,    tone: "text-emerald-300", ring: "border-emerald-500/30 bg-emerald-500/5" },
  ];

  return (
    <div className="grid md:grid-cols-3 gap-4">
      {columns.map((col) => {
        const items = roadmap[col.key] || [];
        const Icon = col.icon;
        return (
          <Card key={col.key} className={`card-elevated p-5 border ${col.ring}`} data-testid={`roadmap-${col.key}`}>
            <div className={`flex items-center gap-2 overline ${col.tone}`}>
              <Icon className="w-3.5 h-3.5" /> {col.label}
            </div>
            {items.length ? (
              <ul className="mt-4 space-y-3">
                {items.map((it, i) => (
                  <li key={`${col.key}-${i}`}>
                    <div className="font-sans-ui font-semibold text-sm">{it.title}</div>
                    {it.detail && <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{it.detail}</div>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground mt-4 italic">No items in this window.</p>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function PendingReviewState({ consultant, results, data }) {
  return (
    <div className="mt-10" data-testid="report-pending-review">
      <Card className="card-elevated p-8">
        <div className="flex items-center gap-2 overline text-amber-300">
          <ShieldCheck className="w-3.5 h-3.5" /> Awaiting consultant review
        </div>
        <h2 className="font-display text-3xl mt-3">
          Your Executive Growth Report™ is with {consultant?.name || "your consultant"} for review
        </h2>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          {consultant?.name || "Your consultant"} at {consultant?.company || "your advisory firm"} personally reviews every Executive Growth Report™ before releasing it — that&apos;s what makes the recommendations actionable rather than generic. You&apos;ll get the full report the moment they approve it, usually within one business day.
        </p>

        {/* Executive Snapshot summary — the leader already saw this on Results, we surface it again for continuity */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Card className="p-5">
            <div className="overline">Business Growth Index™</div>
            <div className="font-display text-4xl text-accent mt-2">{results.overall_score}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{results.risk_level} performance</div>
          </Card>
          <Card className="p-5 md:col-span-2">
            <div className="overline">Top 3 Business Opportunities</div>
            <ul className="mt-3 space-y-2 text-sm">
              {(results.top_gaps || []).map((g, i) => (
                <li key={g.area} className="flex items-center justify-between">
                  <span>{i + 1}. {g.area}</span>
                  <span className="text-muted-foreground">{g.score}/100</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {data.whatsapp_link && (
            <a href={data.whatsapp_link} target="_blank" rel="noreferrer">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full" data-testid="pending-whatsapp">
                <MessageCircle className="w-4 h-4 mr-2" /> Message your consultant
              </Button>
            </a>
          )}
          {data.mailto_link && (
            <a href={data.mailto_link}>
              <Button variant="outline" className="rounded-full" data-testid="pending-email">
                <Mail className="w-4 h-4 mr-2" /> Email your consultant
              </Button>
            </a>
          )}
          {data.booking_link && (
            <a href={data.booking_link} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-full">
                <Calendar className="w-4 h-4 mr-2" /> Book a discovery call
              </Button>
            </a>
          )}
        </div>
      </Card>
    </div>
  );
}

/**
 * Pack 5 · in-flight state shown while `report_status === "generating"`.
 * The Report page polls automatically; this component just renders context.
 */
function GeneratingState({ assessment, data }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="report-generating">
      <Loader2 className="w-10 h-10 text-accent mx-auto animate-spin" />
      <div className="overline mt-8"><span className="accent-line" /> Executive Growth Report™</div>
      <h1 className="font-display text-3xl md:text-4xl mt-3">Preparing your Executive Growth Report™</h1>
      <p className="text-muted-foreground mt-4 leading-relaxed max-w-xl mx-auto">
        Our AI is drafting the executive summary, opportunity analysis, benchmark insights and 30/90/180-day roadmap tailored to <span className="text-foreground">{assessment.country}</span>, <span className="text-foreground">{assessment.industry}</span> and <span className="text-foreground">{assessment.function}</span>.
      </p>
      <p className="text-xs text-muted-foreground mt-6 uppercase tracking-widest">
        Typically ready in 30–60 seconds. This page refreshes automatically.
      </p>

      <Card className="card-elevated mt-10 p-6 text-left" data-testid="generating-cta">
        <div className="flex items-center gap-2 overline">
          <ShieldCheck className="w-3.5 h-3.5 text-accent" /> While you wait
        </div>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
          Your consultant, <span className="text-foreground">{data.consultant?.name}</span>, has been notified and will review the report before releasing it to you. You can already message them via the channels below.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          {data.whatsapp_link && (
            <a href={data.whatsapp_link} target="_blank" rel="noreferrer">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full" data-testid="generating-whatsapp">
                <MessageCircle className="w-4 h-4 mr-2" /> Message your consultant
              </Button>
            </a>
          )}
          {data.mailto_link && (
            <a href={data.mailto_link}>
              <Button variant="outline" className="rounded-full" data-testid="generating-email">
                <Mail className="w-4 h-4 mr-2" /> Email your consultant
              </Button>
            </a>
          )}
        </div>
      </Card>
    </div>
  );
}

/**
 * Pack 5 · terminal-failure state. Consultants (is_owner) get a Retry
 * button that re-dispatches the AI job; business leaders see a friendly
 * message that keeps the messaging channels open.
 */
function FailedState({ assessment, data, isOwner, regenerating, onRetry }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="report-failed">
      <div className="overline text-amber-300"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> Report generation issue</div>
      <h1 className="font-display text-3xl md:text-4xl mt-3">
        {isOwner
          ? "The AI report generation didn't complete"
          : "We're finalising your Executive Growth Report™"}
      </h1>
      <p className="text-muted-foreground mt-4 leading-relaxed">
        {isOwner
          ? "Every retry attempt failed. Click Retry to dispatch a fresh generation — this uses a new AI session. If the issue persists please check the LLM key balance."
          : `${data.consultant?.name || "Your consultant"} is finalising the details of your report and will share it once it's ready. In the meantime, feel free to reach out directly.`}
      </p>
      <p className="text-[10px] text-muted-foreground mt-2 uppercase tracking-widest">
        Context: {assessment.country} · {assessment.industry} · {assessment.function}
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        {isOwner && (
          <Button
            onClick={onRetry}
            disabled={regenerating}
            data-testid="report-retry-btn"
            className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full"
          >
            {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Retry generation
          </Button>
        )}
        {data.whatsapp_link && (
          <a href={data.whatsapp_link} target="_blank" rel="noreferrer">
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full" data-testid="failed-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" /> Message consultant
            </Button>
          </a>
        )}
        {data.mailto_link && (
          <a href={data.mailto_link}>
            <Button variant="outline" className="rounded-full" data-testid="failed-email">
              <Mail className="w-4 h-4 mr-2" /> Email consultant
            </Button>
          </a>
        )}
      </div>
    </div>
  );
}

